import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Form,
  InputNumber,
  Radio,
  Result,
  Spin,
  Space,
  Typography,
  message
} from 'antd'
import {
  CalculatorOutlined,
  DeleteOutlined,
  IdcardOutlined,
  PercentageOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import api from '../utils/api'
import Hint from '../components/common/Hint'

const { Title, Paragraph, Text } = Typography

// ─────────────────────────── styled ───────────────────────────

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 32px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const ContentWrapper = styled.div`
  max-width: 780px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledCard = styled(Card)`
  border-radius: 18px;
  box-shadow: 0 18px 40px rgba(213, 114, 138, 0.12);
  border: 1px solid rgba(213, 114, 138, 0.08);

  .ant-card-head {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  span {
    color: #8c8c8c;
    font-size: 14px;
  }
`

const TierRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: #fff5f7;
  border: 1px solid #f4d6de;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 12px;
`

const TierIndex = styled.span`
  min-width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #d5728a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
`

const RuleRadioGroup = styled(Radio.Group)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .ant-radio-wrapper {
    margin: 0;
    padding: 16px;
    border: 1px solid #eef0f5;
    border-radius: 12px;
    background: #fff;
    transition: all 0.18s ease;
    align-items: flex-start;
    width: 100%;
  }

  .ant-radio-wrapper:hover {
    border-color: #d5728a;
    background: #fffafb;
  }

  .ant-radio-wrapper-checked {
    border-color: #d5728a;
    background: #fff5f7;
    box-shadow: 0 4px 12px rgba(213, 114, 138, 0.12);
  }

  .ant-radio {
    margin-top: 2px;
  }
`

const RuleLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 4px;

  .rule-name {
    font-size: 15px;
    font-weight: 600;
    color: #1c1c1e;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .rule-formula {
    font-size: 13px;
    color: #6b7280;
    font-family: ui-monospace, SFMono-Regular, monospace;
    background: #f4f5f7;
    padding: 6px 10px;
    border-radius: 6px;
    align-self: flex-start;
  }
  .rule-desc {
    font-size: 12px;
    color: #8e8e93;
    line-height: 1.5;
  }
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;

  @media (max-width: 480px) {
    flex-direction: column;
    button {
      width: 100%;
    }
  }
`

const InfoCard = styled(Card)`
  border-radius: 16px;
  background: #ffffffcc;
  border: 1px dashed #f0ccd4;
`

// ─────────────────────────── component ───────────────────────────

const DEFAULT_TIERS = [{ threshold: 0, rate: 0 }]

const ClassTicketCommissionSettingsPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { hasFeature, permissionsLoaded } = useMembership()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [tiers, setTiers] = useState(DEFAULT_TIERS)
  const [commissionRule, setCommissionRule] = useState('sales_rate')
  // 抽成基數：own_sales 自己賣的 / store_total 全店總業績 × 個人比例
  const [basis, setBasis] = useState('own_sales')

  const businessId = user?.businessId

  const fetchSettings = useCallback(async () => {
    if (!businessId) return
    try {
      setLoading(true)
      const res = await api.get(`/api/businesses/${businessId}/class-ticket/commission`)
      const data = res.data?.data || {}
      const loadedTiers =
        Array.isArray(data.tiers) && data.tiers.length > 0
          ? data.tiers
          : DEFAULT_TIERS
      setTiers(loadedTiers)
      setCommissionRule(
        data.commissionRule === 'profit_rate_minus_fee'
          ? 'profit_rate_minus_fee'
          : 'sales_rate'
      )
      setBasis(data.basis === 'store_total' ? 'store_total' : 'own_sales')
    } catch (err) {
      console.error('取得堂票抽成設定失敗', err)
      setTiers(DEFAULT_TIERS)
    } finally {
      setLoading(false)
    }
  }, [businessId])

  useEffect(() => {
    if (hasFeature('salary')) fetchSettings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, hasFeature])

  // ── 權限守衛 ──
  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size='large' tip='正在載入權限...' />
        </div>
      </PageWrapper>
    )
  }

  if (!hasFeature('salary')) {
    return (
      <PageWrapper>
        <ContentWrapper>
          <Result
            status='403'
            title='功能限制'
            subTitle='此功能需要品牌方案才能使用，請升級您的會員方案。'
            extra={
              <Button type='primary' onClick={() => navigate('/settings/membership')}>
                查看會員方案
              </Button>
            }
          />
        </ContentWrapper>
      </PageWrapper>
    )
  }

  // ── 階梯操作 ──
  const handleAddTier = () => {
    setTiers((prev) => [...prev, { threshold: 0, rate: 0 }])
  }

  const handleRemoveTier = (index) => {
    setTiers((prev) => prev.filter((_, i) => i !== index))
  }

  const handleTierChange = (index, field, value) => {
    setTiers((prev) =>
      prev.map((tier, i) =>
        i === index ? { ...tier, [field]: value ?? 0 } : tier
      )
    )
  }

  // ── 儲存 ──
  const handleSubmit = async () => {
    // 基本驗證
    for (let i = 0; i < tiers.length; i++) {
      const { threshold, rate } = tiers[i]
      if (threshold < 0 || rate < 0 || rate > 100) {
        message.error(`第 ${i + 1} 階梯資料有誤：門檻需 ≥ 0，抽成比例需在 0-100% 之間`)
        return
      }
    }
    if (tiers.length === 0) {
      message.error('至少需要一個抽成階梯')
      return
    }

    try {
      setSaving(true)
      await api.put(`/api/businesses/${businessId}/class-ticket/commission`, {
        tiers: tiers.map((t) => ({
          threshold: Number(t.threshold) || 0,
          rate: Number(t.rate) || 0
        })),
        commissionRule,
        basis
      })
      message.success('堂票銷售抽成設定已更新')
    } catch (err) {
      console.error('更新堂票抽成設定失敗', err)
      message.error(
        err.response?.data?.message || err.message || '更新設定失敗，請稍後再試'
      )
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setTiers(DEFAULT_TIERS)
    setCommissionRule('sales_rate')
    setBasis('own_sales')
  }

  // ── render ──
  return (
    <PageWrapper>
      <ContentWrapper>
        <StyledCard
          title={
            <Header>
              <Space>
                <IdcardOutlined style={{ color: '#d5728a', fontSize: 22 }} />
                <Title level={3} style={{ marginBottom: 0, color: '#0f2562' }}>
                  堂票銷售抽成設定
                </Title>
              </Space>
              <span>設定堂票銷售的階梯式抽成比例與計算規則，用於員工薪資報表。</span>
            </Header>
          }
        >
          <Spin spinning={loading}>

            {/* ── 抽成基數 ── */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#1c1c1e', marginBottom: 12 }}>
                抽成基數
              </div>
              <RuleRadioGroup
                value={basis}
                onChange={(e) => setBasis(e.target.value)}
              >
                <Radio value='own_sales'>
                  <RuleLabel>
                    <span className='rule-name'>個人銷售（預設）</span>
                    <span className='rule-formula'>
                      抽成 = 自己賣出的堂票售價 × 比例
                    </span>
                    <span className='rule-desc'>
                      誰賣的就誰抽。比例用員工的「個人堂票抽成」，沒填就用下方階梯。
                    </span>
                  </RuleLabel>
                </Radio>
                <Radio value='store_total'>
                  <RuleLabel>
                    <span className='rule-name'>全店總業績</span>
                    <span className='rule-formula'>
                      抽成 = 全店當月堂票銷售總額 × 個人比例
                    </span>
                    <span className='rule-desc'>
                      不論是誰賣的，全店堂票業績一起算，每個人依自己的比例抽成。
                      <br />
                      只有在「技術服務抽成設定」填了「個人堂票抽成」的人會參與；
                      勾選「員工自收堂票」的範本不算在內。
                    </span>
                  </RuleLabel>
                </Radio>
              </RuleRadioGroup>
            </div>

            {/* ── 計算規則 ── */}
            <Paragraph style={{ color: '#576079', marginBottom: 16 }}>
              選擇抽成計算方式，決定是否從抽成中扣除金流手續費（刷卡費用）。
            </Paragraph>

            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#1c1c1e', marginBottom: 12 }}>
                計算規則
              </div>
              <RuleRadioGroup
                value={commissionRule}
                onChange={(e) => setCommissionRule(e.target.value)}
              >
                <Radio value='sales_rate'>
                  <RuleLabel>
                    <span className='rule-name'>
                      <PercentageOutlined style={{ color: '#3498db' }} />
                      按業績抽成（不扣手續費）
                    </span>
                    <span className='rule-formula'>
                      抽成 = 堂票售價 × 當階抽成%
                    </span>
                    <span className='rule-desc'>
                      不論支付方式為何（現金、刷卡、轉帳），統一以售價乘以抽成比例計算。最簡單的計算方式。
                    </span>
                  </RuleLabel>
                </Radio>
                <Radio value='profit_rate_minus_fee'>
                  <RuleLabel>
                    <span className='rule-name'>
                      <CalculatorOutlined style={{ color: '#d5728a' }} />
                      按業績抽成並扣除手續費（刷卡才扣）
                    </span>
                    <span className='rule-formula'>
                      抽成 = 堂票售價 × 當階抽成% − 金流手續費
                    </span>
                    <span className='rule-desc'>
                      • 客戶以<b>刷卡</b>付款時，自動扣除商家的刷卡手續費。
                      <br />
                      • 客戶以<b>行動支付</b>（Line Pay、街口、Apple Pay 等）付款時，扣除行動支付手續費。
                      <br />
                      • 現金、轉帳支付時手續費為 0，計算結果與規則一相同。
                      <br />
                      • 手續費率在「金流手續費設定」中統一設定。
                    </span>
                  </RuleLabel>
                </Radio>
              </RuleRadioGroup>
            </div>

            {commissionRule === 'profit_rate_minus_fee' && (
              <Hint
                title='提示'
                tone='info'
                style={{ marginBottom: 20, borderRadius: 10 }}
              >
                「扣手續費」規則需要先在「金流手續費設定」中填寫刷卡費率，否則手續費以 0 計算。
              </Hint>
            )}

            {/* ── 階梯設定 ── */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#1c1c1e', marginBottom: 4 }}>
                抽成階梯
              </div>
              <Text type='secondary' style={{ fontSize: 13 }}>
                依員工當月堂票銷售業績總額，套用對應的抽成比例。業績達到門檻時自動升階。
              </Text>
            </div>

            {tiers.map((tier, index) => (
              <TierRow key={index}>
                <TierIndex>{index + 1}</TierIndex>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flex: 1 }}>
                  <Text style={{ whiteSpace: 'nowrap' }}>當月業績達</Text>
                  <InputNumber
                    min={0}
                    value={tier.threshold}
                    onChange={(v) => handleTierChange(index, 'threshold', v)}
                    formatter={(v) => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(v) => v.replace(/\$\s?|(,*)/g, '')}
                    style={{ width: 140 }}
                    placeholder='門檻金額'
                  />
                  <Text style={{ whiteSpace: 'nowrap' }}>元以上，抽成</Text>
                  <InputNumber
                    min={0}
                    max={100}
                    value={tier.rate}
                    onChange={(v) => handleTierChange(index, 'rate', v)}
                    addonAfter='%'
                    style={{ width: 120 }}
                    placeholder='抽成%'
                  />
                </div>
                {tiers.length > 1 && (
                  <Button
                    type='text'
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveTier(index)}
                    title='刪除此階梯'
                  />
                )}
              </TierRow>
            ))}

            <Button
              icon={<PlusOutlined />}
              onClick={handleAddTier}
              style={{ marginBottom: 24, borderColor: '#d5728a', color: '#d5728a' }}
            >
              新增階梯
            </Button>

            <Actions>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                還原預設
              </Button>
              <Button
                type='primary'
                icon={<SaveOutlined />}
                onClick={handleSubmit}
                loading={saving}
                disabled={loading}
              >
                保存設定
              </Button>
            </Actions>
          </Spin>
        </StyledCard>

        {/* ── 說明卡 ── */}
        <InfoCard>
          <Title level={5} style={{ color: '#0f2562', marginBottom: 12 }}>
            設定說明
          </Title>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>• 階梯制：</Text>依員工當月<b>所有堂票銷售業績合計</b>，對照最高適用的門檻來決定抽成比例。
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>• 範例：</Text>設定兩階梯「$ 0 → 5%」「$ 10,000 → 8%」，員工當月售出 $12,000 堂票 → 適用 8%。
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>• 規則一（不扣手續費）：</Text>所有支付方式統一以售價 × 抽成% 計算。
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>• 規則二（扣手續費）：</Text>刷卡與行動支付才扣；現金與轉帳不扣。費率從「金流手續費設定」自動讀取。
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>• 業績歸屬：</Text>依發放堂票時填寫的「銷售人員」欄位判斷。
          </Paragraph>
          <Paragraph>
            <Text strong>• 未設定抽成或抽成 0%：</Text>此員工的堂票抽成將不計入薪資報表。
          </Paragraph>
        </InfoCard>
      </ContentWrapper>
    </PageWrapper>
  )
}

export default ClassTicketCommissionSettingsPage
